import styled from '@emotion/styled';
import React, { useEffect, useRef, useState } from 'react';
import Image from '@base/Image';

interface VideoPlayerProps {
  isPlaying?: boolean;
}

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
`;

const VideoControls = styled.div<VideoPlayerProps>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ isPlaying }) => (isPlaying ? 0 : 1)};
  transition: opacity 0.25s ease;
  pointer-events: ${({ isPlaying }) => (isPlaying ? 'none' : 'auto')};

  button {
    pointer-events: auto;
  }
`;

const PlayPauseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: var(--color-i-white);
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
  &:hover ${VideoControls} {
    opacity: 1;
  }
`;

const VideoPlayer = ({ src }: { src: string }) => {
  const [isPlaying, setIsPlaying] = useState(false as boolean);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false as boolean);
  const videoRef = useRef(null);

  useEffect(() => {
    const unlock = () => {
      videoRef.current?.play();
      window.removeEventListener('click', unlock);
    };
    window.addEventListener('click', unlock);
  }, []);

  useEffect(() => {
    if (videoRef.current && !hasPlayedOnce) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            setHasPlayedOnce(true);
          })
          .catch(() => {
            setIsPlaying(false);
          });
      }
    }
  }, [hasPlayedOnce]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
        if (!hasPlayedOnce) {
          setHasPlayedOnce(true);
        }
      }
    }
  };

  const handleVideoClick = () => {
    handlePlayPause();
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  return (
    <VideoContainer>
      <StyledVideo
        ref={videoRef}
        onPlay={handlePlay}
        onPause={handlePause}
        playsInline
        autoPlay
        controls={false}
      >
        <source src={src} type="video/mp4" />
      </StyledVideo>
      <VideoControls isPlaying={isPlaying}>
        <PlayPauseButton onClick={handleVideoClick}>
          {isPlaying ? (
            <Image src="pause.svg" alt="pause" height={43} width={36} />
          ) : (
            <Image src="play.svg" alt="play" height={43} width={36} />
          )}
        </PlayPauseButton>
      </VideoControls>
    </VideoContainer>
  );
};

export default VideoPlayer;
